import { useEffect, useState } from 'react';
import type {
  AmbitoCategoria,
  Categoria,
  EnvioPrueba,
  EstadoIntegracion,
  GuardarMarca,
  GuardarOrganizacion,
  Inicio2fa,
  LlaveAcceso,
  Marca,
  PreferenciaAviso,
  TipoAviso,
  UsuarioSesion,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import * as notificaciones from '../notificaciones.js';
import type { Diagnostico } from '../notificaciones.js';
import { fechaLarga } from '../componentes.js';
import { Icono, type NombreIcono } from '../componentes/Iconos.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onVolver: () => void;
}

type Apartado = 'organizacion' | 'marca' | 'categorias' | 'avisos' | 'seguridad' | 'integraciones';

interface Entrada {
  clave: Apartado;
  icono: NombreIcono;
  /** Claves de traducción: TODO lo de esta pantalla se dice en el idioma elegido. */
  titulo: string;
  pie: string;
  soloDireccion: boolean;
}

/*
 * El orden es el de la responsabilidad, no el alfabético: primero quién eres
 * como empresa, luego cómo te ves, luego cómo cuentas el dinero, y al final lo
 * de cada persona. «Retribución del equipo» no está aquí a propósito: vive en
 * Finanzas, que es donde se mira junto al resto de los números.
 */
const APARTADOS: Entrada[] = [
  {
    clave: 'organizacion',
    icono: 'edificio',
    titulo: 'aj.organizacion',
    pie: 'aj.organizacionPie',
    soloDireccion: true,
  },
  { clave: 'marca', icono: 'paleta', titulo: 'aj.marca', pie: 'aj.marcaPie', soloDireccion: true },
  {
    clave: 'categorias',
    icono: 'etiqueta',
    titulo: 'aj.categorias',
    pie: 'aj.categoriasPie',
    soloDireccion: true,
  },
  {
    clave: 'avisos',
    icono: 'alerta',
    titulo: 'aj.notificaciones',
    pie: 'aj.notificacionesPie',
    soloDireccion: false,
  },
  {
    clave: 'seguridad',
    icono: 'candado',
    titulo: 'aj.seguridad',
    pie: 'aj.seguridadPie',
    soloDireccion: false,
  },
  {
    clave: 'integraciones',
    icono: 'enchufe',
    titulo: 'aj.integraciones',
    pie: 'aj.integracionesPie',
    soloDireccion: true,
  },
];

/**
 * Ajustes: la empresa a la izquierda, el apartado abierto a la derecha.
 *
 * Los apartados de empresa sólo se listan para Dirección, y no por cortesía: el
 * servidor los rechaza igualmente. Ofrecer una pestaña que va a contestar «sin
 * permiso» es engañar a quien la pulsa.
 *
 * TODA la pantalla habla el idioma elegido, incluido lo que sólo ve Dirección.
 * Un panel a medio traducir se lee como un descuido, y aquí entra gente que no
 * habla castellano.
 */
export function Ajustes({ usuario, idioma, onVolver }: Props) {
  const esDireccion = usuario.rol === 'DIRECTOR';
  const visibles = APARTADOS.filter((a) => !a.soloDireccion || esDireccion);
  const [apartado, setApartado] = useState<Apartado>(esDireccion ? 'organizacion' : 'seguridad');
  const abierto = visibles.find((a) => a.clave === apartado) ?? visibles[0]!;

  const iniciales = usuario.nombre
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'nav.ajustes')}</h1>
          <p>{t(idioma, 'aj.subtitulo')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      <section className="tarjeta ajustes-quien">
        <span className="ajustes-avatar" aria-hidden="true">
          {iniciales}
        </span>
        <span className="ajustes-nombre">
          <b>{usuario.nombre}</b>
          <small>{usuario.email}</small>
        </span>
        <span className="pastilla neutra">{t(idioma, `rol.${usuario.rol}`)}</span>
        <span className="suave pequeno">{usuario.tenantNombre}</span>
      </section>

      <div className="ajustes-maqueta">
        <nav className="ajustes-nav" aria-label={t(idioma, 'aj.apartados')}>
          {visibles.map((a) => (
            <button
              key={a.clave}
              type="button"
              className="ajustes-pestana"
              aria-current={a.clave === abierto.clave ? 'page' : undefined}
              onClick={() => setApartado(a.clave)}
            >
              <span className="ajustes-icono" aria-hidden="true">
                <Icono nombre={a.icono} />
              </span>
              <span className="ajustes-etiqueta">
                <b>{t(idioma, a.titulo)}</b>
                <small>{t(idioma, a.pie)}</small>
              </span>
            </button>
          ))}
        </nav>

        <div className="ajustes-panel">
          {abierto.clave === 'organizacion' && <SeccionOrganizacion idioma={idioma} />}
          {abierto.clave === 'marca' && (
            <SeccionMarca idioma={idioma} tenantId={usuario.tenantId} />
          )}
          {abierto.clave === 'categorias' && <SeccionCategorias idioma={idioma} />}
          {abierto.clave === 'avisos' && <SeccionAvisos idioma={idioma} />}
          {abierto.clave === 'seguridad' && (
            <>
              <SeccionPassword idioma={idioma} />
              <SeccionLlaves idioma={idioma} />
              <SeccionDosFactores idioma={idioma} />
            </>
          )}
          {abierto.clave === 'integraciones' && <SeccionIntegraciones idioma={idioma} />}
        </div>
      </div>
    </main>
  );
}

/** El aviso siempre en el mismo sitio y con la misma voz, apartado a apartado. */
function Aviso({ error, mensaje }: { error: string | null; mensaje: string | null }) {
  return (
    <>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {mensaje && <p className="ok-mensaje">{mensaje}</p>}
    </>
  );
}

/**
 * El mensaje de un error, en el idioma de quien lo lee.
 *
 * El servidor manda una CLAVE, no una frase; traducirla aquí es lo que hace que
 * un fallo se lea igual de bien en alemán que en castellano.
 */
const fallo = (idioma: Idioma, e: unknown, respaldo: string) =>
  e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, respaldo);

// --- Organización ----------------------------------------------------------

/**
 * La identidad fiscal, con el domicilio en piezas.
 *
 * Va separado —dirección, código postal, población, provincia, país— porque en
 * una factura cada pieza ocupa su sitio, y con un solo campo de texto libre
 * habría que adivinar dónde empieza cada una.
 */
function SeccionOrganizacion({ idioma }: { idioma: Idioma }) {
  const [datos, setDatos] = useState<GuardarOrganizacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api
      .organizacion()
      .then(({ organizacion: o }) =>
        setDatos({
          nombre: o.nombre,
          razonSocial: o.razonSocial,
          nif: o.nif,
          direccion: o.direccion,
          codigoPostal: o.codigoPostal,
          poblacion: o.poblacion,
          provincia: o.provincia,
          pais: o.pais,
        }),
      )
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeCargo')));
  }, [idioma]);

  if (!datos) {
    return error ? (
      <Aviso error={error} mensaje={null} />
    ) : (
      <p className="suave">{t(idioma, 'app.cargando')}</p>
    );
  }

  const campo = (clave: keyof GuardarOrganizacion, etiqueta: string, pie?: string) => (
    <div className="campo">
      <label htmlFor={`org-${clave}`}>{t(idioma, etiqueta)}</label>
      <input
        id={`org-${clave}`}
        type="text"
        value={datos[clave] ?? ''}
        onChange={(e) => setDatos({ ...datos, [clave]: e.target.value })}
      />
      {pie && <small className="suave">{t(idioma, pie)}</small>}
    </div>
  );

  const guardar = () => {
    setGuardando(true);
    setError(null);
    setMensaje(null);
    api
      .guardarOrganizacion(datos)
      .then(() => setMensaje(t(idioma, 'aj.guardado')))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')))
      .finally(() => setGuardando(false));
  };

  const incompleta = !datos.razonSocial || !datos.nif || !datos.direccion;

  return (
    <>
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>{t(idioma, 'aj.identidad')}</h2>
          {incompleta && <span className="pastilla ambar">{t(idioma, 'aj.faltanDatos')}</span>}
        </div>
        <p className="suave">{t(idioma, 'aj.identidadPie')}</p>
        <div className="rejilla">
          {campo('nombre', 'aj.nombreComercial', 'aj.nombreComercialPie')}
          {campo('razonSocial', 'aj.razonSocial', 'aj.razonSocialPie')}
          {campo('nif', 'aj.nif', 'aj.nifPie')}
        </div>
      </section>

      <section className="tarjeta">
        <h2>{t(idioma, 'aj.domicilio')}</h2>
        <p className="suave">{t(idioma, 'aj.domicilioPie')}</p>
        <div className="campo">
          <label htmlFor="org-direccion">{t(idioma, 'aj.direccion')}</label>
          <input
            id="org-direccion"
            type="text"
            value={datos.direccion ?? ''}
            onChange={(e) => setDatos({ ...datos, direccion: e.target.value })}
          />
        </div>
        <div className="rejilla">
          {campo('codigoPostal', 'aj.codigoPostal')}
          {campo('poblacion', 'aj.poblacion')}
          {campo('provincia', 'aj.provincia')}
          {campo('pais', 'aj.pais')}
        </div>
      </section>

      <Aviso error={error} mensaje={mensaje} />
      <div className="fila-flex">
        <button type="button" disabled={guardando} onClick={guardar}>
          {t(idioma, guardando ? 'aj.guardando' : 'aj.guardar')}
        </button>
      </div>
    </>
  );
}

// --- Marca y apariencia ----------------------------------------------------

type ClaveColor = 'colorAcento' | 'colorCabecera' | 'colorSecundario';
type ClaveLogo = 'logoClaroId' | 'logoOscuroId';

/*
  Los colores POR DEFECTO son los de la identidad vigente, no los de la
  anterior. Se quedaron con el dorado y el navy viejos cuando la paleta cambio:
  una empresa que no toca nada veia aqui, como propuesta de su marca, dos
  colores que ya no son los del producto.
*/
const COLORES: Array<[ClaveColor, string, string, string]> = [
  ['colorAcento', 'aj.colorAcento', '#b08a4a', 'aj.colorAcentoPie'],
  ['colorCabecera', 'aj.colorCabecera', '#0b1628', 'aj.colorCabeceraPie'],
  ['colorSecundario', 'aj.colorSecundario', '#003c37', 'aj.colorSecundarioPie'],
];

const cambioLogo = (clave: ClaveLogo, valor: string | null): GuardarMarca =>
  clave === 'logoClaroId' ? { logoClaroId: valor } : { logoOscuroId: valor };

/**
 * Los colores y los logotipos, con la vista previa al lado.
 *
 * La previsualización va en la misma pantalla y en vivo porque un color se
 * elige mirándolo, no leyendo su código hexadecimal.
 */
function SeccionMarca({ idioma, tenantId }: { idioma: Idioma; tenantId: string }) {
  const [marca, setMarca] = useState<Marca | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState<ClaveLogo | null>(null);

  useEffect(() => {
    api
      .marca()
      .then(({ marca: m }) => setMarca(m))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeCargo')));
  }, [idioma]);

  if (!marca) {
    return error ? (
      <Aviso error={error} mensaje={null} />
    ) : (
      <p className="suave">{t(idioma, 'app.cargando')}</p>
    );
  }

  const acento = marca.colorAcento ?? '#b08a4a';
  const cabecera = marca.colorCabecera ?? '#0b1628';

  const fijarColor = (clave: ClaveColor, valor: string) =>
    setMarca((m) => (m ? { ...m, [clave]: valor } : m));

  const guardarColores = () => {
    setError(null);
    setMensaje(null);
    api
      .guardarMarca({
        colorAcento: marca.colorAcento,
        colorCabecera: marca.colorCabecera,
        colorSecundario: marca.colorSecundario,
      })
      .then(({ marca: m }) => {
        setMarca(m);
        setMensaje(t(idioma, 'aj.guardado'));
      })
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')));
  };

  const subirLogo = (clave: ClaveLogo, fichero: File) => {
    setSubiendo(clave);
    setError(null);
    setMensaje(null);
    api
      .subirAdjunto('EMPRESA', tenantId, fichero)
      .then(({ adjunto }) => api.guardarMarca(cambioLogo(clave, adjunto.id)))
      .then(({ marca: m }) => setMarca(m))
      .catch((e: unknown) => setError(fallo(idioma, e, 'adjunto.noSeGuarda')))
      .finally(() => setSubiendo(null));
  };

  const quitarLogo = (clave: ClaveLogo) => {
    setError(null);
    api
      .guardarMarca(cambioLogo(clave, null))
      .then(({ marca: m }) => setMarca(m))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')));
  };

  const logo = (clave: ClaveLogo, etiqueta: string, url: string | null, fondo: string) => (
    <div className="campo" key={clave}>
      <span className="etiqueta-campo">{t(idioma, etiqueta)}</span>
      <div className="logo-caja" style={{ background: fondo }}>
        {url ? (
          <img src={url} alt="" />
        ) : (
          <span className="suave pequeno">{t(idioma, 'aj.sinLogotipo')}</span>
        )}
      </div>
      <div className="fila-flex">
        {/* El selector nativo, escondido detrás de un botón que sí se puede leer. */}
        <label className="boton-fichero" htmlFor={`logo-${clave}`}>
          {t(idioma, subiendo === clave ? 'aj.subiendo' : url ? 'aj.cambiar' : 'aj.subir')}
        </label>
        <input
          id={`logo-${clave}`}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="solo-lectores"
          disabled={subiendo !== null}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) subirLogo(clave, f);
          }}
        />
        {url && (
          <button type="button" className="secundario" onClick={() => quitarLogo(clave)}>
            {t(idioma, 'aj.quitar')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="ajuste-marca">
      <div className="ajuste-marca-columna">
        <section className="tarjeta">
          <h2>{t(idioma, 'aj.colores')}</h2>
          <p className="suave">{t(idioma, 'aj.coloresPie')}</p>
          <div className="rejilla">
            {COLORES.map(([clave, etiqueta, defecto, pie]) => {
              const valor = marca[clave] ?? defecto;
              return (
                <div className="campo" key={clave}>
                  <label htmlFor={`color-${clave}`}>{t(idioma, etiqueta)}</label>
                  <div className="ajuste-color">
                    <input
                      id={`color-${clave}`}
                      type="color"
                      value={valor}
                      onChange={(e) => fijarColor(clave, e.target.value)}
                    />
                    <input
                      type="text"
                      aria-label={t(idioma, 'aj.hexadecimal', { campo: t(idioma, etiqueta) })}
                      value={valor}
                      maxLength={7}
                      onChange={(e) => fijarColor(clave, e.target.value)}
                    />
                  </div>
                  <small className="suave">{t(idioma, pie)}</small>
                </div>
              );
            })}
          </div>
          <Aviso error={error} mensaje={mensaje} />
          <div className="fila-flex">
            <button type="button" onClick={guardarColores}>
              {t(idioma, 'aj.guardar')}
            </button>
          </div>
        </section>

        <section className="tarjeta">
          <h2>{t(idioma, 'aj.logotipos')}</h2>
          <p className="suave">{t(idioma, 'aj.logotiposPie')}</p>
          <div className="rejilla">
            {logo('logoClaroId', 'aj.sobreOscuro', marca.logoClaroUrl, cabecera)}
            {logo('logoOscuroId', 'aj.sobreClaro', marca.logoOscuroUrl, '#f5f2ee')}
          </div>
        </section>
      </div>

      <aside className="tarjeta ajuste-previa">
        <h2>{t(idioma, 'aj.vistaPrevia')}</h2>
        <div className="previa-marco">
          <div className="previa-barra" style={{ background: cabecera }}>
            {marca.logoClaroUrl ? <img src={marca.logoClaroUrl} alt="" /> : <b>{marca.nombre}</b>}
            <span>{t(idioma, 'nav.propiedades')}</span>
            <span>{t(idioma, 'nav.panel')}</span>
          </div>
          <div className="previa-cuerpo">
            <div className="previa-linea" style={{ background: acento }} />
            <div className="previa-tarjetas">
              <span />
              <span />
              <span />
            </div>
            <div className="previa-boton" style={{ background: acento }}>
              {t(idioma, 'aj.guardar')}
            </div>
          </div>
        </div>
        <small className="suave">{t(idioma, 'aj.vistaPreviaPie')}</small>
      </aside>
    </div>
  );
}

// --- Categorías de gasto ---------------------------------------------------

/**
 * El plan de gastos de la empresa.
 *
 * Renombrar y desactivar no tocan el histórico: el gasto guarda el CÓDIGO, no
 * una clave ajena. Borrar sólo se ofrece cuando nadie la ha usado nunca; con
 * gastos detrás, lo que corresponde es desactivarla y que los informes de los
 * meses ya cerrados sigan cuadrando.
 */
function SeccionCategorias({ idioma }: { idioma: Idioma }) {
  const [categorias, setCategorias] = useState<Categoria[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nueva, setNueva] = useState('');
  const [ambito, setAmbito] = useState<AmbitoCategoria>('PROPIEDAD');

  const cargar = () =>
    api
      .categorias()
      .then(({ categorias: c }) => setCategorias(c))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeCargo')));

  useEffect(() => {
    void cargar();
    // `cargar` se redefine en cada render; anadirla aqui volveria a pedir las
    // categorias sin parar. Lo que decide es el idioma, y ese esta.
  }, [idioma]);

  if (!categorias) {
    return error ? (
      <Aviso error={error} mensaje={null} />
    ) : (
      <p className="suave">{t(idioma, 'app.cargando')}</p>
    );
  }

  const accion = (promesa: Promise<unknown>) => {
    setError(null);
    void promesa.then(cargar).catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')));
  };

  const grupo = (cual: AmbitoCategoria, titulo: string, pie: string) => {
    const lista = categorias.filter((c) => c.ambito === cual);
    return (
      <section className="tarjeta">
        <div className="tarjeta-cabeza">
          <h2>{t(idioma, titulo)}</h2>
          <span className="suave pequeno numerico">{lista.length}</span>
        </div>
        <p className="suave">{t(idioma, pie)}</p>
        <ul className="lista-categorias">
          {lista.map((c) => (
            <li key={c.id} className={c.activa ? undefined : 'inactiva'}>
              <input
                type="text"
                defaultValue={c.nombre}
                aria-label={t(idioma, 'aj.nombreDe', { nombre: c.nombre })}
                maxLength={80}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== c.nombre) accion(api.editarCategoria(c.id, { nombre: v }));
                }}
              />
              {c.esSistema && <span className="pastilla neutra">{t(idioma, 'aj.sistema')}</span>}
              <span className="suave pequeno numerico">
                {c.gastos === 1
                  ? t(idioma, 'aj.unGasto')
                  : t(idioma, 'aj.nGastos', { total: c.gastos })}
              </span>
              <button
                type="button"
                className="secundario"
                aria-pressed={c.activa}
                title={t(idioma, c.activa ? 'aj.activaAyuda' : 'aj.ocultaAyuda')}
                onClick={() => accion(api.editarCategoria(c.id, { activa: !c.activa }))}
              >
                {t(idioma, c.activa ? 'aj.activa' : 'aj.oculta')}
              </button>
              {/* Sólo si nadie la ha usado: con gastos detrás, borrarla dejaría
                  huecos en informes de meses ya cerrados. */}
              {!c.esSistema && c.gastos === 0 && (
                <button
                  type="button"
                  className="secundario"
                  onClick={() => accion(api.eliminarCategoria(c.id))}
                >
                  {t(idioma, 'aj.borrar')}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    );
  };

  return (
    <>
      <Aviso error={error} mensaje={null} />
      {grupo('PROPIEDAD', 'aj.dePropiedad', 'aj.dePropiedadPie')}
      {grupo('INTERNA', 'aj.internas', 'aj.internasPie')}

      <section className="tarjeta">
        <h2>{t(idioma, 'aj.anadirCategoria')}</h2>
        <p className="suave">{t(idioma, 'aj.categoriasPie2')}</p>
        <div className="fila-flex">
          <input
            type="text"
            placeholder={t(idioma, 'aj.nombreCategoria')}
            aria-label={t(idioma, 'aj.nombreCategoria')}
            value={nueva}
            maxLength={80}
            onChange={(e) => setNueva(e.target.value)}
            style={{ flex: '1 1 14rem' }}
          />
          <select
            value={ambito}
            aria-label={t(idioma, 'aj.ambito')}
            onChange={(e) => setAmbito(e.target.value as AmbitoCategoria)}
          >
            <option value="PROPIEDAD">{t(idioma, 'aj.dePropiedad')}</option>
            <option value="INTERNA">{t(idioma, 'aj.internas')}</option>
          </select>
          <button
            type="button"
            disabled={nueva.trim().length < 2}
            onClick={() => {
              accion(api.crearCategoria({ nombre: nueva.trim(), ambito }));
              setNueva('');
            }}
          >
            {t(idioma, 'ficha.vincular')}
          </button>
        </div>
      </section>
    </>
  );
}

// --- Notificaciones --------------------------------------------------------

/*
 * Esta lista son los avisos que el sistema genera DE VERDAD. Un interruptor
 * para algo que nunca se dispara promete una función que no existe.
 */
const AVISOS: Array<[TipoAviso, NombreIcono]> = [
  ['INCIDENCIA_CRITICA', 'aviso'],
  ['VISITA_VENCIDA', 'calendario'],
  ['GASTO_PENDIENTE', 'billete'],
  ['PAGO_RECURRENTE', 'repetir'],
  ['FACTURA_VENCIDA', 'documento'],
  ['MANTENIMIENTO_VENCIDO', 'llave-inglesa'],
  // Los hechos que suenan en el movil, en el orden en que importan.
  ['INCIDENCIA_NUEVA', 'aviso'],
  ['CLIENTE_INCIDENCIA', 'aviso'],
  ['CLIENTE_ESTANCIA', 'calendario'],
  ['CLIENTE_MENSAJE', 'comentario'],
  ['RESPUESTA_EQUIPO', 'comentario'],
  ['ESTANCIA_NUEVA', 'calendario'],
  ['ASIGNADA_A_TI', 'tareas'],
  ['INCIDENCIA_RESUELTA', 'comprobado'],
  ['GASTO_ALTO', 'billete'],
];

function SeccionAvisos({ idioma }: { idioma: Idioma }) {
  const [preferencias, setPreferencias] = useState<PreferenciaAviso[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
    EL PERMISO ES DE ESTE DISPOSITIVO, no de la cuenta. Quien entra desde el
    movil y desde el ordenador tiene que encenderlo en los dos, y apagarlo en
    uno no apaga el otro. Por eso el interruptor de arriba habla de «este
    dispositivo» y los de abajo, de que tipos de aviso quiere recibir.

    Se guarda el diagnostico ENTERO —estado, dispositivos que conoce el
    servidor y la clave— y no solo el estado: la clave tiene que estar ya en la
    mano cuando se pulse «Activar», porque en el iPhone el permiso solo se
    puede pedir dentro del toque, y esperar una respuesta del servidor antes de
    pedirlo lo gasta.
  */
  const [diag, setDiag] = useState<Diagnostico | null>(null);
  const [cambiandoAvisos, setCambiandoAvisos] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState<EnvioPrueba[] | null>(null);
  const avisos = diag?.estado ?? null;

  const repasar = () => notificaciones.diagnosticar().then(setDiag);
  useEffect(() => {
    void repasar();
  }, []);

  const cambiarAvisos = async () => {
    setCambiandoAvisos(true);
    setPrueba(null);
    try {
      if (avisos === 'encendido') await notificaciones.apagar();
      else await notificaciones.encender(diag?.clave ?? null);
      await repasar();
    } catch {
      setError(t(idioma, 'avisosMovil.noSePudo'));
      await repasar().catch(() => undefined);
    } finally {
      setCambiandoAvisos(false);
    }
  };

  /*
    LA PRUEBA. Es lo unico que convierte «he pulsado Activar» en «me ha sonado
    el telefono». Y cuando no suena, dice por que: el numero que contesta el
    fabricante distingue una clave mal puesta de un dispositivo caducado.
  */
  const probar = async () => {
    setProbando(true);
    setError(null);
    setPrueba(null);
    try {
      const { envios } = await api.pushPrueba();
      setPrueba(envios);
      await repasar();
    } catch (e) {
      setError(fallo(idioma, e, 'avisosMovil.noSePudo'));
    } finally {
      setProbando(false);
    }
  };

  useEffect(() => {
    api
      .preferenciasAviso()
      .then(({ preferencias: p }) => setPreferencias(p))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeCargo')));
  }, [idioma]);

  if (!preferencias) {
    return error ? (
      <Aviso error={error} mensaje={null} />
    ) : (
      <p className="suave">{t(idioma, 'app.cargando')}</p>
    );
  }

  const encendido = (tipo: TipoAviso) => preferencias.find((p) => p.tipo === tipo)?.push ?? true;

  const cambiar = (tipo: TipoAviso) => {
    setError(null);
    api
      .guardarPreferenciaAviso(tipo, !encendido(tipo))
      .then(({ preferencias: p }) => setPreferencias(p))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')));
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'aj.notificaciones')}</h2>
      <p className="suave">{t(idioma, 'aj.avisosPie')}</p>
      <Aviso error={error} mensaje={null} />

      {/* Este dispositivo: el permiso del sistema, que va aparte de los tipos. */}
      <div className="avisos-dispositivo">
        <div className="crecer">
          <b>{t(idioma, 'avisosMovil.titulo')}</b>
          <small className="suave">
            {t(
              idioma,
              avisos === 'encendido'
                ? 'avisosMovil.encendido'
                : avisos === 'denegado'
                  ? 'avisosMovil.denegado'
                  : avisos === 'instalar'
                    ? 'avisosMovil.instalar'
                    : avisos === 'no-soportado'
                      ? 'avisosMovil.noSoportado'
                      : avisos === 'sin-clave'
                        ? 'avisosMovil.sinClave'
                        : 'avisosMovil.apagado',
            )}
          </small>
        </div>
        {(avisos === 'encendido' || avisos === 'apagado') && (
          <button type="button" disabled={cambiandoAvisos} onClick={() => void cambiarAvisos()}>
            {t(idioma, avisos === 'encendido' ? 'avisosMovil.apagar' : 'avisosMovil.encender')}
          </button>
        )}
      </div>

      {/*
        LOS PASOS DEL IPHONE, ESCRITOS.

        «Este navegador no los admite» es verdad y no sirve de nada: lo que hay
        que hacer es añadir la aplicacion a la pantalla de inicio y abrirla
        desde ahi. Mientras eso falte, no hay boton que pulsar, asi que lo unico
        util que cabe aqui son las instrucciones.
      */}
      {avisos === 'instalar' && (
        <ol className="pasos-instalar">
          <li>{t(idioma, 'avisosMovil.paso1')}</li>
          <li>{t(idioma, 'avisosMovil.paso2')}</li>
          <li>{t(idioma, 'avisosMovil.paso3')}</li>
        </ol>
      )}

      {/*
        LO QUE SABE EL SERVIDOR. Sin esto, «activado» en la pantalla y «no hay
        ningun dispositivo» en la base de datos se ven igual, que es justo el
        caso en el que uno se queda esperando avisos que nunca salieron.
      */}
      {diag && (
        <div className="dispositivos-aviso">
          {diag.dispositivos.length === 0 ? (
            <p className="suave">{t(idioma, 'avisosMovil.ninguno')}</p>
          ) : (
            <ul>
              {diag.dispositivos.map((d, i) => (
                <li key={i}>
                  <b>{d.dispositivo || t(idioma, 'avisosMovil.dispositivoSinNombre')}</b>
                  {d.esteDispositivo && <i>{t(idioma, 'avisosMovil.este')}</i>}
                  <small className="suave">
                    {d.usadoEn
                      ? t(idioma, 'avisosMovil.ultimo', { fecha: fechaLarga(d.usadoEn, idioma) })
                      : t(idioma, 'avisosMovil.nuncaRecibio')}
                    {d.fallos > 0 ? ` · ${t(idioma, 'avisosMovil.conFallos', { n: d.fallos })}` : ''}
                  </small>
                </li>
              ))}
            </ul>
          )}
          {diag.dispositivos.length > 0 && (
            <button type="button" className="secundario" disabled={probando} onClick={() => void probar()}>
              <Icono nombre="aviso" />{' '}
              {t(idioma, probando ? 'avisosMovil.probando' : 'avisosMovil.probar')}
            </button>
          )}
          {prueba && (
            <ul className="resultado-prueba">
              {prueba.map((p, i) => (
                <li key={i} className={p.entregado ? 'bien' : 'mal'}>
                  {p.entregado
                    ? t(idioma, 'avisosMovil.entregado', {
                        dispositivo: p.dispositivo || t(idioma, 'avisosMovil.dispositivoSinNombre'),
                      })
                    : t(idioma, 'avisosMovil.rechazado', {
                        dispositivo: p.dispositivo || t(idioma, 'avisosMovil.dispositivoSinNombre'),
                        codigo: p.codigo ?? 0,
                        motivo: p.motivo ?? '',
                      })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ul className="lista-avisos">
        {AVISOS.map(([tipo, icono]) => (
          <li className="fila" key={tipo}>
            <Icono nombre={icono} />
            <span className="crecer">
              <b>{t(idioma, `aviso.${tipo}`)}</b>
              <small>{t(idioma, `aviso.${tipo}Pie`)}</small>
            </span>
            <label className="interruptor">
              <input type="checkbox" checked={encendido(tipo)} onChange={() => cambiar(tipo)} />
              <span className="solo-lectores">{t(idioma, `aviso.${tipo}`)}</span>
              <i aria-hidden="true" />
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Contraseña ------------------------------------------------------------

/**
 * Cambiar la propia contraseña.
 *
 * Está aquí, en Seguridad, y lo tiene todo el mundo: Dirección, un manager y un
 * propietario. Cada uno cambia la suya y nadie cambia la de otro.
 *
 * Se repite la nueva porque un error de tecleo en un campo que no se ve deja a
 * alguien fuera de su cuenta hasta que alguien se la reponga a mano.
 */
function SeccionPassword({ idioma }: { idioma: Idioma }) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const puede = actual.length > 0 && nueva.length >= 8 && repetida.length >= 8;

  const cambiar = () => {
    setError(null);
    setMensaje(null);
    if (nueva !== repetida) {
      setError(t(idioma, 'aj.passwordNoCoincide'));
      return;
    }
    setEnviando(true);
    api
      .cambiarPassword(actual, nueva)
      .then(() => {
        setMensaje(t(idioma, 'aj.passwordCambiada'));
        setActual('');
        setNueva('');
        setRepetida('');
      })
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeGuardo')))
      .finally(() => setEnviando(false));
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'aj.password')}</h2>
      <p className="suave">{t(idioma, 'aj.passwordPie')}</p>
      <Aviso error={error} mensaje={mensaje} />

      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          cambiar();
        }}
      >
        <div className="campo">
          <label htmlFor="pass-actual">{t(idioma, 'aj.passwordActual')}</label>
          <input
            id="pass-actual"
            type="password"
            autoComplete="current-password"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </div>
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="pass-nueva">{t(idioma, 'aj.passwordNueva')}</label>
            <input
              id="pass-nueva"
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
            />
            <small className="suave">{t(idioma, 'aj.passwordMinimo')}</small>
          </div>
          <div className="campo">
            <label htmlFor="pass-repetir">{t(idioma, 'aj.passwordRepetir')}</label>
            <input
              id="pass-repetir"
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
            />
          </div>
        </div>
        <div className="fila-flex">
          <button type="submit" disabled={!puede || enviando}>
            {t(idioma, enviando ? 'aj.guardando' : 'aj.passwordCambiar')}
          </button>
        </div>
      </form>
    </section>
  );
}

// --- Llaves de acceso ------------------------------------------------------

/**
 * Face ID en lugar de contraseña.
 *
 * Lo que se guarda en el servidor es sólo la mitad pública de la llave: la
 * privada no sale nunca del dispositivo. Por eso una llave vale para UN
 * dispositivo —o para los que compartan llavero— y por eso la lista lleva
 * nombre: sin él, quitar la del teléfono que se ha perdido sería adivinar.
 *
 * Una llave no se borra, se revoca y con motivo. Perder un teléfono y quitar su
 * llave tiene que dejar rastro de que ocurrió.
 *
 * YA NO SE DAN DE ALTA LLAVES NUEVAS. Se entra con correo y contraseña, y el
 * gestor del navegador las rellena. Dar de alta una llave que despues no abre
 * ninguna puerta seria ofrecer un acceso que no existe. Lo que si sigue aqui es
 * la lista y la revocacion: quien ya registro una tiene que poder quitarla, y
 * quitarle esa posibilidad por limpiar la pantalla seria quitarle un control de
 * seguridad. El servidor conserva todo el mecanismo por si se reactiva.
 */
function SeccionLlaves({ idioma }: { idioma: Idioma }) {
  const [llaves, setLlaves] = useState<LlaveAcceso[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    api
      .llaves()
      .then((r) => setLlaves(r.llaves))
      .catch(() => setLlaves([]));
  }, []);

  async function revocar(llave: LlaveAcceso) {
    const motivo = window.prompt(
      t(idioma, 'aj.porQueQuitas', { nombre: llave.nombre }),
      t(idioma, 'aj.motivoLlave'),
    );
    if (!motivo || motivo.trim().length < 3) return;
    setError(null);
    setMensaje(null);
    try {
      await api.llaveRevocar(llave.id, motivo.trim());
      setLlaves((previas) => (previas ?? []).filter((l) => l.id !== llave.id));
      setMensaje(t(idioma, 'aj.llaveRetirada'));
    } catch (e) {
      setError(fallo(idioma, e, 'error.servidor'));
    }
  }

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'aj.faceId')}</h2>
      <Aviso error={error} mensaje={mensaje} />
      <p className="suave">{t(idioma, 'aj.faceIdPie')}</p>

      {llaves === null ? (
        <p className="suave">{t(idioma, 'app.cargando')}</p>
      ) : llaves.length === 0 ? (
        <p className="suave">{t(idioma, 'aj.sinLlaves')}</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th>{t(idioma, 'aj.dispositivo')}</th>
              <th>{t(idioma, 'aj.anadida')}</th>
              <th>{t(idioma, 'aj.ultimoUso')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {llaves.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.nombre}
                  {l.respaldada && (
                    <span className="pastilla neutra">{t(idioma, 'aj.enLlavero')}</span>
                  )}
                </td>
                <td>{fechaLarga(l.creadoEn, idioma)}</td>
                <td>{fechaLarga(l.ultimoUsoEn, idioma)}</td>
                <td>
                  <button type="button" className="secundario" onClick={() => void revocar(l)}>
                    {t(idioma, 'aj.quitar')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

    </section>
  );
}

// --- Segundo factor --------------------------------------------------------

function SeccionDosFactores({ idioma }: { idioma: Idioma }) {
  const [activo, setActivo] = useState<boolean | null>(null);
  const [alta, setAlta] = useState<Inicio2fa | null>(null);
  const [codigo, setCodigo] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .dosFactoresEstado()
      .then(({ estado }) => setActivo(estado.activo))
      .catch(() => setError(t(idioma, 'aj.noSeCargo')));
  }, [idioma]);

  async function iniciar() {
    setError(null);
    setMensaje(null);
    try {
      const { alta: a } = await api.dosFactoresIniciar();
      setAlta(a);
    } catch (e) {
      setError(fallo(idioma, e, 'error.servidor'));
    }
  }

  async function confirmar() {
    setError(null);
    try {
      await api.dosFactoresConfirmar(codigo.trim());
      setActivo(true);
      setAlta(null);
      setCodigo('');
      setMensaje(t(idioma, 'aj.guardado'));
    } catch (e) {
      setError(fallo(idioma, e, 'error.servidor'));
    }
  }

  async function desactivar() {
    setError(null);
    try {
      await api.dosFactoresDesactivar(codigo.trim());
      setActivo(false);
      setCodigo('');
      setMensaje(t(idioma, 'aj.guardado'));
    } catch (e) {
      setError(fallo(idioma, e, 'error.servidor'));
    }
  }

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'aj.segundoFactor')}</h2>
      <Aviso error={error} mensaje={mensaje} />

      {activo === null ? (
        <p className="suave">{t(idioma, 'app.cargando')}</p>
      ) : activo ? (
        <>
          <p>
            <span className="pastilla VERDE">{t(idioma, 'aj.activo')}</span>{' '}
            {t(idioma, 'aj.dosFactoresActivo')}
          </p>
          <div className="crm-alta">
            <div className="campo">
              <label htmlFor="tfa-off">{t(idioma, 'aj.codigoDesactivar')}</label>
              <input
                id="tfa-off"
                inputMode="numeric"
                value={codigo}
                placeholder="000000"
                onChange={(ev) => setCodigo(ev.target.value)}
              />
            </div>
            <button type="button" className="secundario" onClick={() => void desactivar()}>
              {t(idioma, 'aj.desactivar')}
            </button>
          </div>
        </>
      ) : alta ? (
        <>
          <p>{t(idioma, 'aj.escanea')}</p>
          <p className="tfa-secreto">
            {t(idioma, 'aj.secreto')}: <code>{alta.secret}</code>
          </p>
          <p className="tfa-uri">
            <code>{alta.uri}</code>
          </p>
          <div className="crm-alta">
            <div className="campo">
              <label htmlFor="tfa-on">{t(idioma, 'aj.codigoConfirmar')}</label>
              <input
                id="tfa-on"
                inputMode="numeric"
                value={codigo}
                placeholder="000000"
                onChange={(ev) => setCodigo(ev.target.value)}
              />
            </div>
            <button type="button" onClick={() => void confirmar()}>
              {t(idioma, 'aj.confirmar')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            <span className="pastilla">{t(idioma, 'aj.inactivo')}</span>{' '}
            {t(idioma, 'aj.dosFactoresInactivo')}
          </p>
          <div className="fila-flex">
            <button type="button" onClick={() => void iniciar()}>
              {t(idioma, 'aj.activarDosFactores')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// --- Integraciones ---------------------------------------------------------

function SeccionIntegraciones({ idioma }: { idioma: Idioma }) {
  const [lista, setLista] = useState<EstadoIntegracion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  const [secreto, setSecreto] = useState('');
  const [etiqueta, setEtiqueta] = useState('');

  function cargar() {
    api
      .integraciones()
      .then(({ integraciones }) => setLista(integraciones))
      .catch((e: unknown) => setError(fallo(idioma, e, 'aj.noSeCargo')));
  }
  useEffect(cargar, [idioma]);

  async function guardar(proveedor: string) {
    if (secreto.trim().length < 1) return;
    setError(null);
    try {
      await api.guardarCredencial(proveedor, {
        secreto: secreto.trim(),
        ...(etiqueta.trim() ? { etiqueta: etiqueta.trim() } : {}),
      });
      setEditando(null);
      setSecreto('');
      setEtiqueta('');
      cargar();
    } catch (e) {
      setError(fallo(idioma, e, 'aj.noSeGuardo'));
    }
  }

  async function desconectar(proveedor: string) {
    setError(null);
    try {
      await api.desconectarIntegracion(proveedor);
      cargar();
    } catch (e) {
      setError(fallo(idioma, e, 'aj.noSeGuardo'));
    }
  }

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'aj.integraciones')}</h2>
      <p className="suave">{t(idioma, 'integraciones.nota')}</p>
      <Aviso error={error} mensaje={null} />
      {lista === null && !error && <p className="suave">{t(idioma, 'app.cargando')}</p>}

      <div className="int-lista">
        {lista?.map((it) => (
          <div key={it.proveedor} className="int-fila">
            <div className="int-info">
              <div className="int-cab">
                <strong>{it.nombre}</strong>
                {it.configurado ? (
                  <span className="pastilla VERDE">{t(idioma, 'integraciones.conectada')}</span>
                ) : (
                  <span className="pastilla">{t(idioma, 'integraciones.sinConectar')}</span>
                )}
              </div>
              <p className="int-desc">{it.descripcion}</p>
            </div>
            <div className="int-acciones">
              <button
                type="button"
                className="secundario"
                onClick={() => {
                  setEditando(editando === it.proveedor ? null : it.proveedor);
                  setSecreto('');
                  setEtiqueta('');
                }}
              >
                {t(idioma, it.configurado ? 'integraciones.cambiar' : 'integraciones.configurar')}
              </button>
              {it.configurado && (
                <button
                  type="button"
                  className="secundario"
                  onClick={() => void desconectar(it.proveedor)}
                >
                  {t(idioma, 'integraciones.desconectar')}
                </button>
              )}
            </div>
            {editando === it.proveedor && (
              <div className="int-form">
                <div className="campo">
                  <label htmlFor={`sec-${it.proveedor}`}>{t(idioma, 'integraciones.clave')}</label>
                  <input
                    id={`sec-${it.proveedor}`}
                    type="password"
                    value={secreto}
                    onChange={(ev) => setSecreto(ev.target.value)}
                    placeholder={t(idioma, 'integraciones.clavePista')}
                  />
                </div>
                <div className="campo">
                  <label htmlFor={`et-${it.proveedor}`}>
                    {t(idioma, 'integraciones.etiqueta')}
                  </label>
                  <input
                    id={`et-${it.proveedor}`}
                    value={etiqueta}
                    onChange={(ev) => setEtiqueta(ev.target.value)}
                  />
                </div>
                <button type="button" onClick={() => void guardar(it.proveedor)}>
                  {t(idioma, 'aj.guardar')}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
